import { Button } from '@flywire/headlessui-tailwindcss';
import { Box } from 'components/Box/Box';
import { FileAnalysisStatus } from 'components/FileAnalysisStatus/FileAnalysisStatus';
import {
  DELETABLE_FILE_STATUSES,
  INVALID_FILE_STATUS,
  NOT_FAILED_FILE_STATUSES,
  SUCCESS_STATUS,
} from 'constants/index';
import { ReactComponent as ArrowDownIcon } from 'icons/arrowDown.svg';
import { ReactComponent as BoldCheckmarkIcon } from 'icons/bold-checkmark.svg';
import { ExclamationCircle } from 'icons/ExclamationCircle';
import type { Document, FieldValueWithLabel } from 'models';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { createMarkup, unescapeHTML, useTranslations } from 'utils';
import { getPaymentOfferFromPayment, isDocVerifyActive } from '../../selectors';
import { DocumentUploader } from './DocumentUploader';

type DocumentCardProps = {
  deleteFile?: (url: string, fileId: string) => void;
  document: Document;
  trackFileDownload?: (fileName: string) => void;
};

const DELETE_LINK_NAME = 'delete';
const DOWNLOAD_LINK_NAME = 'download';
const UPLOAD_LINK_NAME = 'upload';
const documentTypeErrors = ['document_type', 'different_document_type'];

const isValidStatus = (status: string) =>
  NOT_FAILED_FILE_STATUSES.includes(status) || status === INVALID_FILE_STATUS;

const FileErrors = ({
  errors,
  documentType,
}: {
  errors: string[];
  documentType: string;
}) => {
  const i18n = useTranslations();
  const paymentOffer = useSelector(getPaymentOfferFromPayment);
  const hasDocumentTypeError = !!errors.find(error =>
    documentTypeErrors.includes(error),
  );

  const identificationErrorField = errors
    .map(error => error.split(':'))
    .find(
      ([key]) => key === 'payment_option_identification_document_type',
    )?.[1];
  const getErrorMessage = () => {
    if (identificationErrorField) {
      const templateFieldValues = paymentOffer.templateFields?.find(
        field => field?.id === 'payer_id_proof',
      )?.value as FieldValueWithLabel[];
      const identificationErrorFieldLabel = templateFieldValues?.find(
        (template: FieldValueWithLabel) =>
          template.value === identificationErrorField,
      )?.label as string;
      if (identificationErrorFieldLabel) {
        return i18n.t('docVerifyError.documentIdentificationTypeError', {
          documentType: identificationErrorFieldLabel,
        });
      }
    }

    if (hasDocumentTypeError) {
      return i18n.t('docVerifyError.documentTypeError', {
        documentType,
      });
    }

    return i18n.t('docVerifyError.missingInfo');
  };

  const errorMessage = getErrorMessage();

  return (
    <ul className="basis-full list-inside list-disc">
      <li className="ml-1.5 text-sm sm:px-4">{errorMessage}</li>
    </ul>
  );
};

export const DocumentCard = ({
  deleteFile,
  document,
  trackFileDownload,
}: DocumentCardProps) => {
  const [isInfoVisible, setIsInfoVisible] = useState(false);
  const docVerifyActive = useSelector(isDocVerifyActive);
  const i18n = useTranslations();
  const {
    files,
    hyperlinkHref,
    hyperlinkText,
    icon,
    linksForDocument,
    longDescription,
    maxFileSize,
    name,
    required,
    shortDescription,
    type,
  } = document;

  const displayName = document.displayName ?? name;
  const uploadLink = linksForDocument.find(
    link => link.rel === UPLOAD_LINK_NAME,
  );
  const documentUploadURL = uploadLink?.href as string;

  const handleSelectClick = () => setIsInfoVisible(!isInfoVisible);

  const renderFiles = () => {
    const filesToRender = files.map(file => {
      const { linksForFile, fileName, status, errors } = file;

      if (!isValidStatus(status)) {
        return null;
      }

      const downloadLink = linksForFile.find(
        link => link.rel === DOWNLOAD_LINK_NAME,
      );
      const deleteLink = linksForFile.find(
        link => link.rel === DELETE_LINK_NAME,
      );

      const showErrors = docVerifyActive && !!errors?.length;

      return (
        <li key={file.id} className="flex flex-row flex-wrap gap-x-3">
          <div className="flex-1 text-left">
            {downloadLink?.href ? (
              <>
                {showErrors && (
                  <ExclamationCircle
                    aria-hidden={true}
                    className="mr-1.5 text-red-600"
                    width="16"
                    height="16"
                    data-testid={`exclamationCircle-${fileName}`}
                  />
                )}
                <a
                  target="_blank"
                  href={downloadLink.href}
                  onClick={() => trackFileDownload?.(fileName as string)}
                  rel="noopener noreferrer"
                  data-testid={'download-' + fileName}
                >
                  {fileName}
                </a>
              </>
            ) : (
              <p className="m-0 text-left">{fileName}</p>
            )}
            {status === INVALID_FILE_STATUS && (
              <div className="text-left text-xs italic text-red-600">
                {i18n.t('documentCollector.invalidFile')}
              </div>
            )}
          </div>
          {deleteLink?.href && DELETABLE_FILE_STATUSES.includes(status) ? (
            <div className="flex gap-x-1.5 items-center">
              <button
                onClick={() => deleteFile?.(deleteLink.href, file.id)}
                data-testid={'delete-' + file.id}
              >
                <span className="Icon Icon--close Icon--xs margin-0" />
              </button>
            </div>
          ) : (
            <FileAnalysisStatus status={status} />
          )}
          {showErrors && <FileErrors errors={errors} documentType={type} />}
        </li>
      );
    });

    return <ul className="DocumentCollector-filesList">{filesToRender}</ul>;
  };

  const hasSuccessStatus = files.some(file => file.status === SUCCESS_STATUS);
  const documentFiles = files.filter(file => isValidStatus(file.status));
  const anyFilesWithErrors =
    documentFiles.some(file => !!file.errors?.length) && docVerifyActive;

  const hasValidFiles = documentFiles.length > 0;
  const shouldHideUploader = document.multiFile === false && hasValidFiles;

  const renderIcon = () => {
    if (anyFilesWithErrors) {
      return (
        <div className="DocumentCollector-documentIcon">
          <ExclamationCircle
            data-testid="exclamationCircleIcon"
            className="w-6 h-6 text-red-600"
          />
        </div>
      );
    }

    if (hasSuccessStatus) {
      return (
        <div className="DocumentCollector-checkmarkIcon">
          <BoldCheckmarkIcon data-testid="boldCheckmarkIcon" />
        </div>
      );
    }

    return (
      <div
        className="DocumentCollector-documentIcon"
        dangerouslySetInnerHTML={{ __html: unescapeHTML(icon) }}
        data-testid={`documentIcon${name}`}
      />
    );
  };

  return (
    <Box className="p-0" data-testid="documentCard">
      {anyFilesWithErrors && (
        <div className="bg-[#FCF4DB] px-[18px] py-2">
          <span
            className="text-xs"
            dangerouslySetInnerHTML={createMarkup(
              i18n.t('docVerifyError.documentType.errorNote'),
            )}
          ></span>
        </div>
      )}
      <div className="mb-[16px] p-[18px]">
        <div className="DocumentCollector-fileType">
          <div className="DocumentCollector-fileTypeContent">
            {renderIcon()}
            <div className="DocumentCollector-documentDescription">
              <div
                className="DocumentCollector-documentName"
                data-testid={`document-${type}`}
              >
                {displayName}
                {required && (
                  <span className="DocumentCollector-required">
                    {i18n.t('documentCollector.required')}
                  </span>
                )}
              </div>
              <div className="DocumentCollector-shortDescription">
                {shortDescription}
              </div>
              <a
                className="DocumentCollector-hyperLink"
                href={hyperlinkHref}
                target="_blank"
                rel="noreferrer"
              >
                {hyperlinkText}
              </a>
            </div>
          </div>
          <div className="DocumentCollector-selectButtonWrapper">
            <Button
              data-testid={`selectButton-${document.id}`}
              tone="secondary"
              size="full"
              onClick={handleSelectClick}
            >
              {i18n.t('documentCollector.selectButtonText')}
              <ArrowDownIcon
                className={`DocumentCollector-selectButtonArrow${
                  isInfoVisible ? ' is-open' : ''
                }`}
              />
            </Button>
          </div>
        </div>
        {isInfoVisible && (
          <div
            className="DocumentCollector-documentText"
            data-testid="extendedInfo"
          >
            <hr />
            <div
              className="DocumentCollector-longDescription"
              dangerouslySetInnerHTML={createMarkup(longDescription)}
            ></div>
            <div className="DocumentCollector-documentUploader">
              {!shouldHideUploader && (
                <DocumentUploader
                  files={files}
                  documentUploadURL={documentUploadURL}
                  maxFileSize={maxFileSize}
                />
              )}
              {renderFiles()}
            </div>
          </div>
        )}
      </div>
    </Box>
  );
};
